import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import TrainingTimer from "./TrainingTimer";
import { useParams } from "react-router-dom";
import { createClient } from "@supabase/supabase-js";
import XP from "./xp";
import Topbar from "./Topbar";
import Footer from "./Footer";
import Loader from "./Loader";
const apiBase = process.env.REACT_APP_API_URL;
const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_KEY);

export default function TrainingCenter() {
  const { leagueId } = useParams();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [training, setTraining] = useState({});
  const [teamId, setTeamId] = useState("");
  const [trainingAll, setTrainingAll] = useState(false);

  const fetchTeam = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      setLoading(true);
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const token = session?.access_token;

        const res = await axios.get(`${apiBase}/api/region/team/${leagueId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const teamData = res.data?.user_fantasy_team_players || [];

        const formatted = teamData.map((p) => {
          const potential = p.players?.potential_level || 1;

          // === NEW: handle equipped skin ===
          const equippedSkins = p.players?.user_equipped_player_skins;
          const skinData = equippedSkins?.[0]?.player_skins ?? null;
          const playerImageUrl = skinData ? `${skinData.full_preview}` : `${p.players.player_image}`;

          const progressArray = Array.isArray(p.player_training_progress) ? p.player_training_progress : [p.player_training_progress].filter(Boolean);

          const active = progressArray.find((r) => r.training_in_progress);
          const completed = progressArray
            .filter((r) => !r.training_in_progress)
            .sort((a, b) => new Date(b.last_trained_at || 0) - new Date(a.last_trained_at || 0))[0];

          const xp = Number(completed?.xp ?? 0);
          const currentLevel = completed?.training_level ?? 0;

          return {
            player_id: p.player_id,
            team_player_id: p.id,
            player_name: p.players?.name || "Unknown",
            position: p.players?.role || "—",
            potential_level: potential,
            player_image: playerImageUrl, // Use equipped skin if exists
            equipped_skin: skinData, // keep metadata for rarity/name if needed
            team_color: p.players?.teams?.color ?? "255,255,255",
            xp,
            team_logo: p.players?.teams?.team_logo || "",
            training_level: currentLevel,
            training_in_progress: active?.training_in_progress ?? false,
            training_ends_at: active?.training_ends_at ?? null,
            last_trained_at: completed?.last_trained_at ?? null,
          };
        });

        const roleOrder = ["Top", "Jng", "Mid", "Bot", "Sup"];

        const sorted = [...formatted].sort((a, b) => roleOrder.indexOf(a.position) - roleOrder.indexOf(b.position));

        setPlayers(sorted);
        setTeamId(res.data?.id);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load team");
      } finally {
        setLoading(false);
      }
    },
    [leagueId],
  );

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  const handleStartTraining = async (playerId, teamPlayerId) => {
    setTraining((prev) => ({ ...prev, [playerId]: true }));
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      const res = await axios.post(
        `${apiBase}/api/region/train/start/${leagueId}`,
        { playerId, teamPlayerId, teamId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(res.data.message);
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to start training");
    } finally {
      setTraining((prev) => ({ ...prev, [playerId]: false }));
    }
  };

  const handleTrainAll = async () => {
    const eligible = players.filter((p) => !p.training_in_progress && p.training_level < p.potential_level);
    if (!eligible.length) return;
    setTrainingAll(true);
    await Promise.allSettled(eligible.map((p) => handleStartTraining(p.player_id, p.team_player_id)));
    setTrainingAll(false);
  };

  const eligibleCount = players.filter((p) => !p.training_in_progress && p.training_level < p.potential_level).length;

  if (loading) return <Loader loadingTitle={"Loading Practice Room"} showLogo={true} />;

  return (
    <div className="page-container" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Topbar leagueId={leagueId} showLinks={true} />
      <div className="main">
        <div>
          <h4 style={{ color: "#fff", marginBottom: "10px" }}>PRACTICE SCRIMS</h4>
          <p style={{ color: "#8899a8", margin: "0px", fontSize: "14px" }}>Scrims increase your player skill (+2% Bonus /each lvl)</p>
          <p style={{ color: "#8899a8", fontSize: "14px" }}>
            Any levels earned after a match starts will not count towards scoring for that matchday.
          </p>
        </div>
        <div className="training-center">
          <div className="training-grid">
            {players.map((p) => {
              const xp = Number(p.xp) || 0;
              const potential = p.potential_level;
              const level = p.training_level; // trust the backend
              const isMaxLevel = level >= potential;

              // XP needed for NEXT level
              const xpForNext = XP.LEVEL_XP[level] ?? 0;

              // XP already spent to reach this level
              const xpBefore = XP.getTotalXPForLevel(level);

              // XP progress inside this level
              const xpIntoLevel = Math.max(xp - xpBefore, 0);

              // Progress %
              const progress = xpForNext > 0 ? Math.min((xpIntoLevel / xpForNext) * 100, 100) : 100;

              // Predict next training
              const xpGain = isMaxLevel ? 0 : XP.TRAINING_XP;
              const projectedXP = Math.min(xpIntoLevel + xpGain, xpForNext);

              const projectedProgress = xpForNext > 0 ? Math.min((projectedXP / xpForNext) * 100, 100) : 100;

              const percentGain = Math.max(projectedProgress - progress, 0).toFixed(1);
              const xpRemaining = Math.max(xpForNext - xpIntoLevel, 0);

              return (
                <div
                  key={p.player_id}
                  className="player-card"
                  style={{
                    display: "flex",
                    position: "relative",
                    minWidth: "200px",

                    // alignItems: "stretch",
                    // flexDirection: "row",
                    overflow: "hidden",
                    // boxShadow:
                    //   " rgba(0, 0, 0, 0.4) 0px -4px 8px -2px inset, rgba(236, 233, 233, 0.2) -1px 3px 6px -2px inset,  rgba(0, 0, 0, 0.2) 0px 30px 20px -30px",
                    background: `linear-gradient(
  130deg,
  rgba(${p.team_color}, 0.3),
rgb(7 13 23) 70%
)`,
                  }}>
                  <img
                    src={`/teams/${p.team_logo}`}
                    alt={p.player_name}
                    style={{
                      position: "absolute",
                      height: "280px",
                      width: "280px",
                      objectFit: "contain",
                      opacity: "0.1",
                      left: "-40px",
                      top: "-20px",
                      zIndex: "1",
                    }}
                    className="team-bg-logo"
                  />
                  <img src={`/roles/${p.position}.svg`} alt={p.role} style={{ width: "21px", height: "21px", borderRadius: "0", zIndex: "90" }} />
                  <p style={{ margin: "0", color: "rgb(255, 180, 92)", zIndex: "90", fontSize: "14px", fontWeight: "500" }}>Potential Skill Lvl:</p>
                  <div className="level-hexagon" style={{ marginBottom: "0" }}>
                    <p>{p.potential_level}</p>
                  </div>
                  {isMaxLevel && <small style={{ color: "#facc15", fontWeight: "600" }}>⭐ Max Potential Reached ⭐</small>}
                  <img src={`/players/${p.player_image}`} alt={p.player_name} className="player-image" style={{ zIndex: "90" }} />
                  <div className="player-info">
                    <div>
                      <h4 style={{ margin: "0", color: "white" }}>{p.player_name}</h4>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <div className="level-hexagon" style={{ marginBottom: "0" }}>
                        <p>{level}</p>
                      </div>
                      <div
                        className="average-performance-bubble"
                        style={{
                          background:
                            "conic-gradient(from 0deg, #ffe3c4 6%, #ffb45c 18%, #ff912f 32%, #ffca00 46%, #ffd100 58%, #ffd9b2 72%, #ff8a18 84%, #ff7f1f 92%)",
                          border: "1px solid #ffffff38",
                        }}>
                        <p style={{ color: "white", margin: "0", textShadow: "0px 0px 4px rgba(0, 0, 0, 0.6)", fontWeight: "700" }}>+{level * 2}%</p>
                      </div>
                    </div>
                    {p.training_in_progress && p.training_ends_at ? (
                      <TrainingTimer
                        playerId={p.player_id}
                        leagueId={leagueId}
                        trainingEndsAt={p.training_ends_at}
                        onCompleted={() => fetchTeam(true)}
                      />
                    ) : isMaxLevel ? (
                      <button
                        disabled
                        className="edit-btn"
                        style={{
                          margin: "10px",
                          opacity: 0.5,
                          cursor: "not-allowed",
                        }}>
                        Max Level
                      </button>
                    ) : (
                      <button
                        disabled={training[p.player_id]}
                        onClick={() => handleStartTraining(p.player_id, p.team_player_id, teamId)}
                        className="edit-btn"
                        style={{ margin: "10px", width: "100%" }}>
                        Train
                      </button>
                    )}
                    {!p.training_in_progress && !isMaxLevel && (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <small style={{ color: "white" }}>
                          Level {level}/{potential}
                          <span style={{ color: "#00ff90" }}>{projectedProgress >= 100 ? " ▲ Level Up!" : ` +${percentGain}% next session`}</span>
                        </small>

                        <small style={{ color: "#9ca3af", fontSize: "12px" }}>
                          +{XP.TRAINING_XP} XP next • {xpRemaining.toFixed(0)} XP to level {level + 1}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <button
            className="edit-btn"
            onClick={handleTrainAll}
            disabled={trainingAll || eligibleCount === 0}
            style={{ marginTop: "10px", opacity: eligibleCount === 0 ? 0.5 : 1, width: "100%" }}>
            {trainingAll ? "Starting..." : `Train All (${eligibleCount})`}
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
}
